const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, iframe, audio[controls], video[controls], [tabindex]:not([tabindex="-1"])';

// Intersection-aria flickity logic is duplicated in assets/theme.dev.js.liquid (theme carousels) — keep both in sync.
const INTERSECTION_ARIA_SELECTOR = '[data-flickity-intersection-aria="true"]';

function rectOverlapsRoot(el, rootEl) {
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) {
    return false;
  }
  if (!rootEl || typeof rootEl.getBoundingClientRect !== 'function') {
    const w = document.documentElement.clientWidth;
    const h = document.documentElement.clientHeight;
    return r.right > 0.5 && r.bottom > 0.5 && r.left < w - 0.5 && r.top < h - 0.5;
  }
  const rr = rootEl.getBoundingClientRect();
  return r.right > rr.left + 0.5 && r.bottom > rr.top + 0.5 && r.left < rr.right - 0.5 && r.top < rr.bottom - 0.5;
}

function setFlickityCellAriaHidden(el, hidden) {
  if (hidden) {
    el.setAttribute('aria-hidden', 'true');
  } else {
    el.removeAttribute('aria-hidden');
  }
}

/**
 * Flickity 3 unconditionally writes aria-hidden in Cell.select / Cell.unselect, racing any external sync.
 * For cells inside [data-flickity-intersection-aria="true"], we strip that aria mutation so our geometry sync
 * is the sole owner of aria-hidden. Other Flickity carousels keep Flickity's original behavior.
 */
let cellPrototypePatched = false;
function patchFlickityCellPrototypeOnce(flkty) {
  if (cellPrototypePatched) {
    return;
  }
  const cells = flkty.cells;
  if (!cells || !cells.length) {
    return;
  }
  const proto = Object.getPrototypeOf(cells[0]);
  if (!proto || typeof proto.select !== 'function' || typeof proto.unselect !== 'function') {
    return;
  }
  cellPrototypePatched = true;
  const origSelect = proto.select;
  const origUnselect = proto.unselect;

  proto.select = function patchedFlickityCellSelect() {
    const el = this.element;
    if (el && typeof el.closest === 'function' && el.closest(INTERSECTION_ARIA_SELECTOR)) {
      el.classList.add('is-selected');
      return;
    }
    origSelect.call(this);
  };

  proto.unselect = function patchedFlickityCellUnselect() {
    const el = this.element;
    if (el && typeof el.closest === 'function' && el.closest(INTERSECTION_ARIA_SELECTOR)) {
      el.classList.remove('is-selected');
      return;
    }
    origUnselect.call(this);
  };
}

export function bindFlickityHiddenCellInert(flkty) {
  if (!flkty || !flkty.element || flkty.element.getAttribute('data-flickity-skip-inert') === 'true') {
    return;
  }
  const supportsInert = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;
  const useIntersectionAria = flkty.element.getAttribute('data-flickity-intersection-aria') === 'true';
  const visibilityRoot = flkty.viewport && flkty.viewport.nodeType === 1 ? flkty.viewport : null;

  if (useIntersectionAria) {
    patchFlickityCellPrototypeOnce(flkty);
    (flkty.cells || []).forEach(cell => {
      if (cell && cell.element) {
        cell.element.removeAttribute('aria-hidden');
      }
    });
  }

  const syncInertFromAria = () => {
    const cells = flkty.cells || [];
    cells.forEach(cell => {
      const el = cell.element;
      if (!el) return;
      const hidden = el.getAttribute('aria-hidden') === 'true';
      if (supportsInert) {
        el.inert = hidden;
        return;
      }
      if (hidden) {
        el.querySelectorAll(FOCUSABLE_SELECTOR).forEach(node => {
          if (!node.hasAttribute('data-flickity-inert-tabindex')) {
            const prev = node.hasAttribute('tabindex') ? node.getAttribute('tabindex') : '';
            node.setAttribute('data-flickity-inert-tabindex', prev);
          }
          node.setAttribute('tabindex', '-1');
        });
      } else {
        el.querySelectorAll('[data-flickity-inert-tabindex]').forEach(node => {
          const prev = node.getAttribute('data-flickity-inert-tabindex');
          node.removeAttribute('data-flickity-inert-tabindex');
          if (prev === '') {
            node.removeAttribute('tabindex');
          } else {
            node.setAttribute('tabindex', prev);
          }
        });
      }
    });
  };

  const applyAriaFromGeometry = () => {
    (flkty.cells || []).forEach(cell => {
      const el = cell.element;
      if (!el) return;
      setFlickityCellAriaHidden(el, !rectOverlapsRoot(el, visibilityRoot));
    });
  };

  const flushAriaAndInert = () => {
    if (useIntersectionAria) {
      applyAriaFromGeometry();
    }
    syncInertFromAria();
  };

  if (useIntersectionAria) {
    flushAriaAndInert();
    const onLayoutChange = () => requestAnimationFrame(flushAriaAndInert);
    flkty.on('ready', onLayoutChange);
    flkty.on('change', onLayoutChange);
    flkty.on('resize', onLayoutChange);
    flkty.on('settle', onLayoutChange);
    document.addEventListener('theme:resize:width', onLayoutChange);
    const teardownAriaExtras = () => document.removeEventListener('theme:resize:width', onLayoutChange);
    flkty.on('destroy', teardownAriaExtras);
    flkty.on('deactivate', teardownAriaExtras);
    return;
  }

  const onSync = () => requestAnimationFrame(syncInertFromAria);
  flkty.on('ready', onSync);
  flkty.on('change', onSync);
  flkty.on('settle', onSync);
  syncInertFromAria();
}
